import { createRouter, createWebHistory, START_LOCATION } from 'vue-router'
import ability from '@/config/ability'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import Access from '../views/Access.vue'
import Agreement from '../views/agreement/Agreement.vue'
import EditAgreement from '../views/agreement/EditAgreement.vue'
import AppointmentManager from '../views/appointments/AppointmentManager.vue'
import { getDefaultDashboardForRoles } from '@/utilities/dashboards.ts'

// Calendars
import AppointmentsCalendar from '../views/appointments/AppointmentsCalendar.vue'
import AvailabilityCalendar from '../views/appointments/AvailabilityCalendar.vue'
import ForgotPassword from '../views/auth/ForgotPassword.vue'
import LogIn from '../views/auth/LogIn.vue'
import PasswordReset from '../views/auth/PasswordReset.vue'
import Verify from '../views/auth/Verify.vue'
import Campaign from '../views/campaigns/Campaign.vue'
import Campaigns from '../views/campaigns/Campaigns.vue'
// Conversations
import MissedConversations from '../views/conversations/MissedConversations.vue'
// Flow, Campaigns and Leads
import Flow from '../views/Flow.vue'
// Finance
import Invoicing from '../views/finance/Invoicing.vue'
import MainLayout from '../views/MainLayout.vue'
import Notifications from '../views/Notifications.vue'
// Dashboards
import DashboardLayout from '../views/DashboardLayout.vue'
import AdminDashboard from '../views/dashboards/admin/AdminDashboard.vue'
import AgentDashboard from '../views/dashboards/call-center/AgentDashboard.vue'
import CallCenterDashboard from '../views/dashboards/call-center/CallCenterDashboard.vue'
import CompanyDashboard from '../views/dashboards/retain/CompanyDashboard.vue'
import SalesExecutiveDashboard from '../views/dashboards/dealership/SalesExecutiveDashboard.vue'
import DealershipDashboard from '../views/dashboards/dealership/DealershipDashboard.vue'
import GroupDashboard from '../views/dashboards/dealership/GroupDashboard.vue'
// Reports
import Reports from '../views/Reports.vue'
import AppointmentsList from '../views/reports/appointments/AppointmentsList.vue'
import CancelledNoShow from '../views/reports/appointments/CancelledNoShow.vue'
import MethodConversions from '../views/reports/appointments/MethodConversions.vue'
import SourceConversions from '../views/reports/appointments/SourceConversions.vue'
import LiveAgentCalls from '../views/reports/call-centers/LiveAgentCalls.vue'
import AppointedCustomers from '../views/reports/customers/AppointedCustomers.vue'
import DealershipData from '../views/reports/dealerships/Data.vue'
import DealershipStatistics from '../views/reports/dealerships/Statistics.vue'
import TargetsResults from '../views/reports/dealerships/TargetsResults.vue'
import AppointmentInvites from '../views/reports/engagement/AppointmentInvites.vue'
import Calls from '../views/reports/engagement/Calls.vue'
import Emails from '../views/reports/engagement/Emails.vue'
import Insights from '../views/reports/engagement/Insights.vue'
import InviteSummary from '../views/reports/engagement/InviteSummary.vue'
import Sms from '../views/reports/engagement/Sms.vue'
import Summary from '../views/reports/engagement/Summary.vue'
import ReloopOverview from '../views/reports/reloop/Overview.vue'
import ReloopScorecard from '../views/reports/reloop/Scorecard.vue'
import ReloopSubmissions from '../views/reports/reloop/Submissions.vue'
import AgentKpi from '../views/reports/staff/AgentKpi.vue'
import AgentStatistics from '../views/reports/staff/AgentStatistics.vue'
import AllocationsReport from '../views/reports/staff/Allocations.vue'
import StatusActivity from '../views/reports/staff/StatusActivity.vue'
import StatusSummary from '../views/reports/staff/StatusSummary.vue'
import StatusTimeline from '../views/reports/staff/StatusTimeline.vue'
import RolesPermissions from '../views/reports/users/RolesPermissions.vue'
import ReAuctionList from '../views/reports/vehicles/ReAuctionList.vue'
import RoiCalculator from '../views/reports/RoiCalculator.vue'
// Settings
import Settings from '../views/Settings.vue'
import CronJobs from '../views/settings/admin/CronJobs.vue'
import SocketServer from '../views/settings/admin/SocketServer.vue'
import CallCenter from '../views/settings/call-centers/CallCenter.vue'
import CallCenters from '../views/settings/call-centers/CallCenters.vue'
import EmailAccounts from '../views/settings/communications/EmailAccounts.vue'
import PhoneNumbers from '../views/settings/communications/PhoneNumbers.vue'
import Dealership from '../views/settings/dealerships/Dealership.vue'
import Dealerships from '../views/settings/dealerships/Dealerships.vue'
import Groups from '../views/settings/dealerships/Groups.vue'
import Manufacturers from '../views/settings/dealerships/Manufacturers.vue'
import DealershipTargets from '../views/settings/dealerships/targets/Targets.vue'
import BillingAccounts from '../views/settings/finance/BillingAccounts.vue'
import RepeatingInvoice from '../views/settings/finance/RepeatingInvoice.vue'
import RepeatingInvoices from '../views/settings/finance/RepeatingInvoices.vue'
import Schedule from '../views/settings/schedule/Schedule.vue'
import ScheduleTemplate from '../views/settings/schedule/ScheduleTemplate.vue'
import ExternalDealershipSystems from '../views/settings/security/ExternalDealershipSystems.vue'
import UserSettings from '../views/settings/users/User.vue'
import Users from '../views/settings/users/Users.vue'
import RolesPermissionsSettings from '../views/settings/users/RolesPermissions.vue'
import Models from '../views/settings/vehicles/Models.vue'
// Tasks
import Tasks from '../views/Tasks.vue'
import ReAuction from '../views/vehicles/reauction/ReAuction.vue'
import ReAuctions from '../views/vehicles/reauction/ReAuctions.vue'
import UsedStock from '../views/vehicles/stock/UsedStock.vue'
import Valuation from '../views/vehicles/valuations/Valuation.vue'
// Vehicles
import Valuations from '../views/vehicles/valuations/Valuations.vue'
import VehicleModelsBrowser from '../views/vehicles/Models.vue'
// Customers and agreements
import Customers from '../views/customers/Customers.vue'
// DEV TESTS
import BlankView from '../views/dev-tests/BlankView.vue'
import Testing from '../views/dev-tests/Testing.vue'
import User from '../views/dev-tests/User.vue'

// Random
import HealthCheck from '../views/HealthCheck.vue'
// Error pages
import Maintenance from '../views/error/MaintenanceMode.vue'
import NoAccess from '../views/error/NoAccess.vue'
import NotFound from '../views/error/NotFound.vue'

const routes = [
  {
    path: '/login',
    name: 'Log In',
    component: LogIn
  },
  {
    path: '/verify/:token',
    name: 'Verify',
    component: Verify
  },
  {
    path: '/forgot-password',
    name: 'Forgot Password',
    component: ForgotPassword
  },
  {
    path: '/password-reset/:token',
    name: 'Password Reset',
    component: PasswordReset
  },
  {
    path: '/access',
    name: 'Access',
    component: Access
  },
  {
    path: '',
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '/tests/testing',
        name: 'Testing',
        component: Testing
      },
      {
        path: '/notifications',
        name: 'Notifications',
        component: Notifications
      },
      {
        path: '/tasks/:tab?',
        name: 'Tasks',
        component: Tasks
      },
      {
        path: '/customers',
        name: 'Customers',
        component: Customers,
        meta: { canGates: ['view_customers'] }
      },
      {
        path: '/customers/flow',
        name: 'Flow',
        component: Flow,
        meta: { canGates: ['view_customers', 'view_flow'] }
      },
      {
        path: '/customers/campaigns',
        name: 'Campaigns',
        component: Campaigns,
        meta: { canGates: ['view_customers', 'view_campaigns'] }
      },
      {
        path: '/customers/campaigns/:campid',
        name: 'Campaign',
        component: Campaign,
        meta: { canGates: ['view_customers', 'view_campaigns'] }
      },
      {
        path: '/appointments/manager',
        name: 'Appointment Manager',
        component: AppointmentManager,
        meta: { canGates: ['view_appointments', 'assign_appointments'] }
      },
      {
        path: '/appointments/calendar',
        name: 'Appointments Calendar',
        component: AppointmentsCalendar,
        meta: { canGates: ['view_appointments'] }
      },

      {
        path: '/appointments/availability',
        name: 'Availability Calendar',
        component: AvailabilityCalendar,
        meta: { canGates: ['view_availability'] }
      },
      {
        path: '/conversations/missed',
        name: 'Missed Conversations',
        component: MissedConversations,
        meta: { canGates: ['view_all_missed_contacts'] }
      },
      {
        path: '/agreement/:agid/:tab?/:id?',
        name: 'Agreement',
        component: Agreement,
        meta: { canGates: ['view_agreement'] }
      },
      {
        path: '/agreement-edit/:id/:tab?',
        name: 'Edit Agreement',
        component: EditAgreement,
        meta: { canGates: ['view_agreement', 'edit_agreement_details'] }
      },
      {
        path: '/vehicles/valuations',
        name: 'Valuations',
        component: Valuations,
        meta: { canGates: ['view_valuations'] }
      },
      {
        path: '/vehicles/valuations/:id',
        name: 'Valuation',
        component: Valuation,
        meta: { canGates: ['view_valuations'] }
      },
      {
        path: '/vehicles/stock/used',
        name: 'Used Stock',
        component: UsedStock,
        meta: { canGates: ['view_vehicles_used_stock'] }
      },
      {
        path: '/vehicles/reauction',
        name: 'ReAuctions',
        component: ReAuctions,
        meta: { canGates: ['view_valuations'] }
      },
      {
        path: '/vehicles/reauction/:id',
        name: 'ReAuction',
        component: ReAuction,
        meta: { canGates: ['view_valuations'] }
      },
      {
        path: '/vehicles/models',
        name: 'Vehicle Models',
        component: VehicleModelsBrowser,
        meta: { canGates: ['view_vehicle_models'] }
      },
      {
        path: 'reports/finance/invoicing',
        name: 'Invoicing',
        component: Invoicing,
        meta: { canGates: ['view_finances'] }
      },
      {
        path: '/reports',
        name: 'Reports',
        component: Reports,
        meta: { canGates: ['view_reports'] }
      },
      {
        path: '/reports/call-agents/statistics',
        name: 'Statistics',
        component: AgentStatistics,
        meta: { canGates: ['view_reports', 'view_reports_agents'] }
      },
      {
        path: '/reports/call-agents/kpis',
        name: 'KPIs',
        component: AgentKpi,
        meta: { canGates: ['view_reports', 'view_reports_agents'] }
      },
      {
        path: '/reports/call-agents/allocations',
        name: 'Allocations & Availability',
        component: AllocationsReport,
        meta: { canGates: ['view_reports', 'view_reports_agents'] }
      },
      {
        path: '/reports/call-agents/status-timeline',
        name: 'Status Timeline',
        component: StatusTimeline,
        meta: { canGates: ['view_reports', 'view_reports_agents'] }
      },
      {
        path: '/reports/call-agents/status-summary',
        name: 'Status Summary',
        component: StatusSummary,
        meta: { canGates: ['view_reports', 'view_reports_agents'] }
      },
      {
        path: '/reports/call-agents/status-activity',
        name: 'Status & Activity',
        component: StatusActivity,
        meta: { canGates: ['view_reports', 'view_reports_agents'] }
      },
      {
        path: '/reports/engagement/calls',
        name: 'Calls',
        component: Calls,
        meta: { canGates: ['view_reports', 'view_reports_engagement'] }
      },
      {
        path: '/reports/engagement/sms',
        name: 'SMS',
        component: Sms,
        meta: { canGates: ['view_reports', 'view_reports_engagement'] }
      },
      {
        path: '/reports/engagement/emails',
        name: 'Emails',
        component: Emails,
        meta: { canGates: ['view_reports', 'view_reports_engagement'] }
      },
      {
        path: '/reports/engagement/summary',
        name: 'Summary',
        component: Summary,
        meta: { canGates: ['view_reports', 'view_reports_engagement'] }
      },
      {
        path: '/reports/engagement/insights',
        name: 'Insights',
        component: Insights,
        meta: { canGates: ['view_reports', 'view_reports_engagement'] }
      },
      {
        path: '/reports/engagement/invite-summary',
        name: 'Invite Summary',
        component: InviteSummary,
        meta: { canGates: ['view_reports', 'view_reports_engagement'] }
      },
      {
        path: '/reports/engagement/appointment-invites',
        name: 'Appointment Invites',
        component: AppointmentInvites,
        meta: { canGates: ['view_reports', 'view_reports_engagement'] }
      },
      {
        path: '/reports/call-centers/live-agent-calls',
        name: 'Live Agent Calls',
        component: LiveAgentCalls,
        meta: { canGates: ['view_reports', 'view_reports_call_centers'] }
      },
      {
        path: '/reports/dealerships/statistics',
        name: 'Dealership Statistics',
        component: DealershipStatistics,
        meta: { canGates: ['view_reports', 'view_reports_dealerships'] }
      },
      {
        path: '/reports/dealerships/targets-and-results',
        name: 'Targets & Results',
        component: TargetsResults,
        meta: { canGates: ['view_reports', 'view_reports_dealerships'] }
      },
      {
        path: '/reports/dealerships/data',
        name: 'Dealership Data',
        component: DealershipData,
        meta: { canGates: ['view_reports', 'view_reports_dealerships'], userTypesAllowed: [1] }
      },
      {
        path: '/reports/customers/appointed-customers',
        name: 'Appointed Customers',
        component: AppointedCustomers,
        meta: { canGates: ['view_reports', 'view_reports_customers'] }
      },
      {
        path: '/reports/reloop/overview',
        name: 'Overview',
        component: ReloopOverview,
        meta: { canGates: ['view_reports', 'view_reports_reloop'] }
      },
      {
        path: '/reports/reloop/submissions',
        name: 'Submissions',
        component: ReloopSubmissions,
        meta: { canGates: ['view_reports', 'view_reports_reloop'] }
      },
      {
        path: '/reports/reloop/scorecard',
        name: 'Scorecard',
        component: ReloopScorecard,
        meta: { canGates: ['view_reports', 'view_reports_reloop'] }
      },
      {
        path: '/reports/appointments/list',
        name: 'Appointments List',
        component: AppointmentsList,
        meta: { canGates: ['view_reports', 'view_reports_appointments'] }
      },
      {
        path: '/reports/appointments/cancelled-and-no-show',
        name: 'Cancelled & No-Show',
        component: CancelledNoShow,
        meta: { canGates: ['view_reports', 'view_reports_appointments'] }
      },
      {
        path: '/reports/appointments/method-conversions',
        name: 'Method Conversions',
        component: MethodConversions,
        meta: { canGates: ['view_reports', 'view_reports_appointments'] }
      },
      {
        path: '/reports/appointments/source-conversions',
        name: 'Source Conversions',
        component: SourceConversions,
        meta: { canGates: ['view_reports', 'view_reports_appointments'] }
      },
      {
        path: '/reports/vehicles/reauction-list',
        name: 'ReAuction List',
        component: ReAuctionList,
        meta: { canGates: ['view_reports', 'view_reports_finance'] }
      },
      {
        path: '/reports/users/roles-permissions',
        name: 'Roles & Permissions List',
        component: RolesPermissions,
        meta: { canGates: ['view_settings', 'manage_roles_and_permissions'] }
      },
      {
        path: '/reports/roi-calculator',
        name: 'ROI Calculator',
        component: RoiCalculator
      },
      {
        path: '/settings',
        name: 'Settings',
        component: Settings,
        meta: { canGates: ['view_settings'] }
      },
      {
        path: '/settings/users',
        name: 'Users',
        component: Users,
        meta: { canGates: ['view_settings', 'manage_users'] }
      },
      {
        path: '/settings/users/:id/:tab?',
        name: 'User Settings',
        component: UserSettings,
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          const isOwnSettingsPage = Number(to.params.id) === Number(authStore.details.id)
          if (isOwnSettingsPage || (ability.can('view_settings') && ability.can('manage_users'))) {
            return next()
          }

          // If they don't match, deny access
          return next({ path: '/no-access' })
        }
      },
      {
        path: '/settings/users/roles-permissions',
        name: 'Roles & Permissions',
        component: RolesPermissionsSettings,
        meta: { canGates: ['view_settings', 'manage_users'] }
      },
      {
        path: '/settings/schedule',
        name: 'Schedule',
        component: Schedule,
        meta: { canGates: ['view_settings', 'manage_user_schedule'] }
      },
      {
        path: '/settings/schedule/template',
        name: 'Schedule Template',
        component: ScheduleTemplate,
        meta: { canGates: ['view_settings', 'manage_user_schedule'] }
      },
      {
        path: '/settings/dealerships',
        name: 'Dealerships',
        component: Dealerships,
        meta: { canGates: ['view_settings', 'manage_dealerships'] }
      },
      {
        path: '/settings/dealerships/:id/:tab?',
        name: 'Dealership',
        component: Dealership,
        meta: { canGates: ['view_settings', 'manage_dealerships'] }
      },
      {
        path: '/settings/dealership/targets',
        name: 'Dealership Targets',
        component: DealershipTargets,
        meta: { canGates: ['view_settings', 'manage_dealerships'] }
      },
      {
        path: '/settings/call-centers',
        name: 'Call Centers',
        component: CallCenters,
        meta: { canGates: ['view_settings', 'manage_call_centers'] }
      },
      {
        path: '/settings/call-centers/:id/:tab?',
        name: 'Call Centre',
        component: CallCenter,
        meta: { canGates: ['view_settings', 'manage_call_centers'] }
      },
      {
        path: '/settings/phone-numbers',
        name: 'Phone Numbers',
        component: PhoneNumbers,
        meta: { canGates: ['view_settings', 'manage_dealerships'] }
      },
      {
        path: '/settings/email-accounts',
        name: 'Email Accounts',
        component: EmailAccounts,
        meta: { canGates: ['view_settings', 'manage_dealerships'] }
      },
      {
        path: '/settings/admin/cron-jobs',
        name: 'CronJobs',
        component: CronJobs,
        meta: { canGates: ['administer_system'] }
      },
      {
        path: '/settings/admin/socket-server',
        name: 'Socket Server',
        component: SocketServer,
        meta: { canGates: ['administer_system'] }
      },
      {
        path: '/settings/security/external-dealership-systems',
        name: 'External Dealership Systems',
        component: ExternalDealershipSystems,
        meta: { canGates: ['view_external_systems'] }
      },
      {
        path: '/settings/manufacturers',
        name: 'Manufacturers',
        component: Manufacturers,
        meta: { canGates: ['view_settings', 'manage_manufacturers'] }
      },
      {
        path: '/settings/vehicles/models',
        name: 'Models',
        component: Models,
        meta: { canGates: ['view_settings'] }
      },
      {
        path: '/settings/groups',
        name: 'Groups',
        component: Groups,
        meta: { canGates: ['view_settings', 'manage_dealer_groups'] }
      },
      {
        path: '/settings/finance/billing-accounts',
        name: 'Billing Accounts',
        component: BillingAccounts,
        meta: { canGates: ['view_settings', 'manage_finances'] }
      },
      {
        path: '/settings/finance/repeating-invoices',
        name: 'Invoice Templates',
        component: RepeatingInvoices,
        meta: { canGates: ['view_settings', 'manage_finances'] }
      },
      {
        path: '/settings/finance/repeating-invoice/:val',
        name: 'Invoice',
        component: RepeatingInvoice,
        meta: { canGates: ['view_settings', 'manage_finances'] }
      },
      {
        path: '/dashboard',
        component: DashboardLayout,
        children: [
          {
            path: 'call-center/agent',
            name: 'Agent Dashboard',
            component: AgentDashboard,
            meta: { canGates: [] }
          },
          {
            path: 'call-center',
            name: 'Call Centre Dashboard',
            component: CallCenterDashboard,
            meta: { canGates: [] }
          },
          {
            path: 'admin',
            name: 'Admin Dashboard',
            component: AdminDashboard,
            meta: { canGates: [] }
          },
          {
            path: 'company',
            name: 'Company Dashboard',
            component: CompanyDashboard,
            meta: { canGates: [] }
          },
          {
            path: 'dealership/sales-executive',
            name: 'Sales Executive Dashboard',
            component: SalesExecutiveDashboard,
            meta: { canGates: [] }
          },
          {
            path: 'dealership',
            name: 'Dealership Dashboard',
            component: DealershipDashboard,
            meta: { canGates: [] }
          },
          {
            path: 'dealership/group',
            name: 'Group Dashboard',
            component: GroupDashboard,
            meta: { canGates: [] }
          }
        ]
      }
    ]
  },
  {
    path: '/no-access',
    name: 'No Access',
    component: NoAccess
  },
  {
    path: '/maintenance',
    name: 'Maintenance Mode',
    component: Maintenance
  },
  {
    path: '/home',
    redirect: '/'
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'Not Found',
    component: NotFound
  },
  // DEV TESTS & PAGES
  {
    path: '/tests/blank',
    name: 'BlankView',
    component: BlankView
  },
  {
    path: '/tests/user',
    name: 'User',
    component: User
  },
  {
    path: '/healthcheck',
    name: 'App Health Check',
    component: HealthCheck
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

router.beforeEach(async (to, from, next) => {
  document.title = `${to.name}`

  const authStore = useAuthStore()
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  // On initial app load (ONLY), verify user
  if (from === START_LOCATION) {
    // If initially loaded route requires auth, verify user
    if (requiresAuth) {
      await authStore.verify()

      if (!authStore.isLoggedIn) {
        authStore.signOut()
        return next({ path: '/login', query: { ref: to.fullPath } })
      } else if (authStore.isLoggedIn) {
        // If the route is "/" for an authenticated user, redirect to /home
        if (to.path === '/') {
          const defaultDashboard = getDefaultDashboardForRoles(authStore.roles)
          return next({ path: defaultDashboard.route })
        }
        try {
          if (to.meta.canGates) {
            const can = to.meta.canGates.every(permission => ability.can(permission))

            if (!can) {
              throw new Error('No access')
            }
          }
          if (to.meta.userTypesAllowed) {
            const can = to.meta.userTypesAllowed.includes(authStore.details.userType)
            if (!can) {
              throw new Error('No access')
            }
          }
          return next()
        } catch (error) {
          console.error('Ability update failed:', error)
          return next({ path: '/no-access' })
        }
      }
    }
  }

  if (to.path === '/') {
    const defaultDashboard = getDefaultDashboardForRoles(authStore.roles)
    return next({ path: defaultDashboard.route })
  }

  return next()
})

export default router
